import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axiosInstance";
import "./EditPayroll.scss";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import Select from "react-select";

// Warning Popup Component
const PayrollChangeWarningPopup = ({
  isOpen,
  onConfirm,
  onCancel,
  payrollName,
}) => {
  if (!isOpen) return null;

  return (
    <div className="warning-popup-overlay">
      <div className="warning-popup">
        <div className="warning-header">
          <div className="warning-icon">⚠️</div>
          <h3>Confirm Payroll Change</h3>
        </div>
        <div className="warning-content">
          <p>
            You are about to change the payroll type to{" "}
            <strong>"{payrollName}"</strong>.
          </p>
          <div className="warning-details">
            <p>
              <strong>This action will:</strong>
            </p>
            <ul>
              <li>Reset all YTD (Year-to-Date) amounts to 0</li>
              <li>Replace current payroll components</li>
              <li>Recalculate all payroll values</li>
              <li>Clear existing payroll history for this employee</li>
            </ul>
          </div>
          <p className="warning-note">
            <strong>Note:</strong> This action cannot be undone. Please ensure
            you want to proceed.
          </p>
        </div>
        <div className="warning-actions">
          <button className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button className="confirm-button" onClick={onConfirm}>
            Yes, Change Payroll
          </button>
        </div>
      </div>
    </div>
  );
};

const EditPayroll = ({
  employee,
  closeModal,
  showToast,
  fetchEmployeeData,
}) => {
  const [loading, setLoading] = useState(true);
  const [grossSalary, setGrossSalary] = useState("");
  const [netSalary, setNetSalary] = useState(0);
  const [employeeDeductions, setEmployeeDeductions] = useState(0);
  const [employerDeductions, setEmployerDeductions] = useState(0);
  const [components, setComponents] = useState([]);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [payrollOptions, setPayrollOptions] = useState([]);
  const [currentPayrollId, setCurrentPayrollId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [payrollType, setPayrollType] = useState("");
  const [originalData, setOriginalData] = useState(null);
  const [grossSalaryChanged, setGrossSalaryChanged] = useState(false);

  // New state for warning popup
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [pendingPayrollChange, setPendingPayrollChange] = useState(null);
  const [hasShownWarning, setHasShownWarning] = useState(false);

  const fetchPayrollData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/setup/${employee.id}`,
      );
      const data = response.data;
      console.log(response);

      // Store original data
      setOriginalData(data);

      setGrossSalary(data.grossSalary || 0);
      setNetSalary(data.netSalary || 0);
      setEmployeeDeductions(data.employeeDeductions || 0);
      setEmployerDeductions(data.employerDeductions || 0);
      setCurrentPayrollId(data.payrollId || null);

      const mappedComponents = (data.components || []).map((component) => ({
        ...component,
        value: component.amount || "",
      }));
      setComponents(mappedComponents);

      // Set response data to show existing details for AUTO payroll
      if (data.payrollId) {
        setResponseData({
          data: {
            grossSalary: data.grossSalary || 0,
            netSalary: data.netSalary || 0,
            employeeDeductions: data.employeeDeductions || 0,
            employerDeductions: data.employerDeductions || 0,
            ctc: data.ctc || 0,
          },
        });
      }

      return data.payrollId;
    } catch (error) {
      console.error("Error fetching payroll data:", error);
      showToast("Error fetching payroll data", "error");
      return null;
    }
  }, [employee.id, showToast]);

  const fetchPayrollOptions = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/payroll/names`,
      );
      setPayrollOptions(response.data.data);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching payroll options:", error);
      showToast("Error fetching payroll options", "error");
      return [];
    }
  }, [showToast]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [payrollData, options] = await Promise.all([
          fetchPayrollData(),
          fetchPayrollOptions(),
        ]);

        if (payrollData && options.length > 0) {
          const currentPayroll = options.find(
            (option) => option.payrollId === payrollData,
          );

          if (currentPayroll) {
            const selectedOption = {
              ...currentPayroll,
              label: `${currentPayroll.payrollName} – ${currentPayroll.payrollSetupType}`,
              value: currentPayroll.payrollId,
            };
            setSelectedPayroll(selectedOption);
            setPayrollType(currentPayroll.payrollName);

            // For MANUAL payroll, we need to fetch the component structure
            // since the existing components from fetchPayrollData might not have the complete structure
            if (currentPayroll.payrollSetupType === "MANUAL") {
              // Get the original data that was just fetched
              const response = await axiosInstance.get(
                `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/setup/${employee.id}`,
              );
              const originalPayrollData = response.data;
              console.log(response.data);

              // Call the function directly here without depending on it in useEffect
              setTimeout(async () => {
                const selectedPayroll = options.find(
                  (option) => option.payrollName === currentPayroll.payrollName,
                );

                if (selectedPayroll && originalPayrollData.grossSalary) {
                  setIsCalculating(true);
                  try {
                    const calcResponse = await axiosInstance.post(
                      `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/calculate`,
                      {
                        employeeId: employee.id,
                        payrollId: selectedPayroll.payrollId,
                        grossSalary: parseFloat(
                          originalPayrollData.grossSalary,
                        ),
                        branchId: 0,
                        employeePaymentSetupDTO: [],
                      },
                    );

                    // Since this is the current payroll, merge existing values with component structure
                    const componentInputs =
                      calcResponse.data.data.components?.map((comp) => {
                        // Find existing component value from originalData
                        const existingComp =
                          originalPayrollData?.components?.find(
                            (existing) =>
                              existing.salaryName === comp.salaryName,
                          );

                        return {
                          ...comp,
                          value:
                            existingComp?.amount || existingComp?.value || "",
                          // Keep existing IDs if available
                          paymentSetupId:
                            existingComp?.paymentSetupId ||
                            comp.paymentSetupId ||
                            0,
                        };
                      }) || [];

                    setComponents(componentInputs);
                  } catch (error) {
                    console.error("Manual payroll calculate error:", error);
                    showToast(
                      error.response?.data?.message ||
                        "An error occurred while loading payroll components. Please try again later.",
                      "error",
                    );
                  } finally {
                    setIsCalculating(false);
                  }
                }
              }, 100);
            }
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPayrollData, fetchPayrollOptions]);

  const validateComponents = () => {
    return components.every((component) => component.value !== "");
  };

  // Helper function to check if only gross salary has changed
  const isOnlyGrossSalaryChanged = () => {
    if (!originalData) return false;

    // Check if payroll hasn't changed
    const isPayrollSame = !isCurrentPayrollChanged();

    // Check if gross salary is different
    const isGrossDifferent =
      parseFloat(grossSalary) !== parseFloat(originalData.grossSalary || 0);

    // Check if other fields haven't changed (for legacy mode)
    const isNetSalaryUnchanged =
      !payrollType &&
      parseFloat(netSalary) === parseFloat(originalData.netSalary || 0);
    const isEmployeeDeductionsUnchanged =
      !payrollType &&
      parseFloat(employeeDeductions) ===
        parseFloat(originalData.employeeDeductions || 0);
    const isEmployerDeductionsUnchanged =
      !payrollType &&
      parseFloat(employerDeductions) ===
        parseFloat(originalData.employerDeductions || 0);

    // Check if components haven't changed
    const areComponentsUnchanged = components.every((component, index) => {
      const originalComponent = originalData.components?.[index];
      return (
        parseFloat(component.value || 0) ===
        parseFloat(originalComponent?.amount || originalComponent?.value || 0)
      );
    });

    return (
      isPayrollSame &&
      isGrossDifferent &&
      (!payrollType
        ? isNetSalaryUnchanged &&
          isEmployeeDeductionsUnchanged &&
          isEmployerDeductionsUnchanged
        : true) &&
      areComponentsUnchanged
    );
  };

  // New function to handle salary-only update
  const handleSalaryOnlyUpdate = async () => {
    const selectedPayrollOption = payrollOptions.find(
      (option) => option.payrollName === payrollType,
    );

    // Determine which payrollId to use
    const payrollIdToUse = selectedPayrollOption?.payrollId || currentPayrollId;

    setIsSubmitting(true);
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/update/salary`,
        {
          payrollId: payrollIdToUse,
          employeeId: employee.id,
          grossSalary: parseFloat(grossSalary),
          branchId: 0,
          employeePaymentSetupDTO: [],
        },
      );
      fetchEmployeeData();
      closeModal();
      showToast("Salary updated successfully!", "success");
    } catch (error) {
      console.error("Error updating salary:", error);
      showToast(
        error.response?.data?.message ||
          "An error occurred while updating salary. Please try again later.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGrossSalaryChange = (e) => {
    const value = e.target.value;
    setGrossSalary(value);

    // Check if gross salary is different from original and payroll is already selected
    const isGrossSalaryDifferent =
      originalData &&
      parseFloat(value) !== parseFloat(originalData.grossSalary);
    const hasPayrollSelected = payrollType && !isCurrentPayrollChanged();

    setGrossSalaryChanged(isGrossSalaryDifferent && hasPayrollSelected);

    // Only trigger recalculation for AUTO payroll when gross salary changes
    if (payrollType && value) {
      const selectedPayroll = payrollOptions.find(
        (option) => option.payrollName === payrollType,
      );

      if (selectedPayroll?.payrollSetupType === "AUTO") {
        // Clear existing response data first
        setResponseData(null);
        // Trigger recalculation for AUTO payroll
        handleAutoPayroll(payrollType, value);
      }
      // For MANUAL payroll, don't auto-recalculate, just clear response data
      else if (selectedPayroll?.payrollSetupType === "MANUAL") {
        setResponseData(null);
      }
    }
  };

  // Modified handlePayrollTypeChange to show warning popup
  const handlePayrollTypeChange = (e) => {
    const selectedPayrollType = e.target.value;
    const selectedPayrollOption = payrollOptions.find(
      (option) => option.payrollName === selectedPayrollType,
    );
    const isPayrollChanged =
      selectedPayrollOption?.payrollId !== currentPayrollId;

    // If payroll is being changed and warning hasn't been shown yet
    if (isPayrollChanged && !hasShownWarning && selectedPayrollType) {
      setPendingPayrollChange({
        payrollType: selectedPayrollType,
        payrollOption: selectedPayrollOption,
      });
      setShowWarningPopup(true);
      return; // Don't proceed with the change yet
    }

    // Proceed with the payroll type change
    proceedWithPayrollChange(
      selectedPayrollType,
      selectedPayrollOption,
      isPayrollChanged,
    );
  };

  // Function to actually handle the payroll type change after confirmation
  const proceedWithPayrollChange = (
    selectedPayrollType,
    selectedPayrollOption,
    isPayrollChanged,
  ) => {
    setPayrollType(selectedPayrollType);
    setResponseData(null);

    // Reset gross salary change warning when payroll type changes
    setGrossSalaryChanged(false);

    if (isPayrollChanged) {
      // Reset components when changing payroll type
      setComponents([]);

      // Fetch and show components for MANUAL payroll
      if (selectedPayrollOption?.payrollSetupType === "MANUAL" && grossSalary) {
        handleManualPayrollCalculate(selectedPayrollType, grossSalary);
      }
      // Auto-calculate for AUTO payroll
      else if (
        selectedPayrollOption?.payrollSetupType === "AUTO" &&
        grossSalary
      ) {
        handleAutoPayroll(selectedPayrollType, grossSalary);
      }
    } else {
      // If same payroll is selected, restore original data
      if (originalData && !isPayrollChanged) {
        const mappedComponents = (originalData.components || []).map(
          (component) => ({
            ...component,
            value: component.amount || "",
          }),
        );
        setComponents(mappedComponents);

        // Restore response data for AUTO payroll
        if (selectedPayrollOption?.payrollSetupType === "AUTO") {
          setResponseData({
            data: {
              grossSalary: originalData.grossSalary || 0,
              netSalary: originalData.netSalary || 0,
              employeeDeductions: originalData.employeeDeductions || 0,
              employerDeductions: originalData.employerDeductions || 0,
              ctc: originalData.ctc || 0,
            },
          });
        }
        // For MANUAL payroll, components are already restored above
      }
    }
  };

  // Handle warning popup confirmation
  const handleWarningConfirm = () => {
    setShowWarningPopup(false);
    setHasShownWarning(true); // Mark that warning has been shown

    if (pendingPayrollChange) {
      const selectedOption = {
        ...pendingPayrollChange.payrollOption,
        label: `${pendingPayrollChange.payrollOption.payrollName} – ${pendingPayrollChange.payrollOption.payrollSetupType}`,
        value: pendingPayrollChange.payrollOption.payrollId,
      };
      setSelectedPayroll(selectedOption);

      const isPayrollChanged =
        pendingPayrollChange.payrollOption?.payrollId !== currentPayrollId;
      proceedWithPayrollChange(
        pendingPayrollChange.payrollType,
        pendingPayrollChange.payrollOption,
        isPayrollChanged,
      );
    }
    setPendingPayrollChange(null);
  };

  // Handle warning popup cancellation
  const handleWarningCancel = () => {
    setShowWarningPopup(false);
    setPendingPayrollChange(null);

    // Revert to the previous payroll selection
    if (currentPayrollId && payrollOptions.length > 0) {
      const currentPayroll = payrollOptions.find(
        (option) => option.payrollId === currentPayrollId,
      );

      if (currentPayroll) {
        const selectedOption = {
          ...currentPayroll,
          label: `${currentPayroll.payrollName} – ${currentPayroll.payrollSetupType}`,
          value: currentPayroll.payrollId,
        };
        setSelectedPayroll(selectedOption);
      }
    } else {
      // If no current payroll, clear the selection
      setSelectedPayroll(null);
    }
  };

  const handleManualPayrollCalculate = async (payrollType, grossSalary) => {
    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType,
    );
    if (selectedPayroll && grossSalary) {
      setIsCalculating(true);
      try {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/calculate`,
          {
            employeeId: employee.id,
            payrollId: selectedPayroll.payrollId,
            grossSalary: parseFloat(grossSalary),
            branchId: 0,
            employeePaymentSetupDTO: [],
          },
        );

        // For manual payroll, set empty values or existing values
        const isCurrentPayroll = selectedPayroll.payrollId === currentPayrollId;
        let componentInputs;

        if (isCurrentPayroll && originalData?.components) {
          // Use existing values if it's the current payroll
          componentInputs =
            response.data.data.components?.map((comp) => {
              const existingComp = originalData.components.find(
                (existing) => existing.salaryName === comp.salaryName,
              );
              return {
                ...comp,
                value: existingComp?.amount || "",
              };
            }) || [];
        } else {
          // Empty values for new payroll
          componentInputs =
            response.data.data.components?.map((comp) => ({
              ...comp,
              value: "",
            })) || [];
        }

        setComponents(componentInputs);
        setResponseData(response.data);
      } catch (error) {
        console.error("Manual payroll calculate error:", error);
        showToast(
          error.response?.data?.message ||
            "An error occurred while calculating payroll components. Please try again later.",
          "error",
        );
      } finally {
        setIsCalculating(false);
      }
    }
  };

  const handleAutoPayroll = async (payrollType, grossSalary) => {
    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType,
    );
    if (selectedPayroll && grossSalary) {
      setIsCalculating(true);
      try {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/calculate`,
          {
            employeeId: employee.id,
            payrollId: selectedPayroll.payrollId,
            grossSalary: parseFloat(grossSalary),
            branchId: 0,
            employeePaymentSetupDTO: [],
          },
        );
        setComponents(response.data.data.components || []);
        setResponseData(response.data);
      } catch (error) {
        console.error("Auto payroll error:", error);
        showToast(
          error.response?.data?.message ||
            "An error occurred. Please try again later.",
          "error",
        );
      } finally {
        setIsCalculating(false);
      }
    }
  };

  const handleManualSubmit = async () => {
    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType,
    );
    if (!validateComponents()) {
      showToast("Please fill in all component values.", "error");
      return;
    }

    // Check if only gross salary changed for manual payroll
    if (isOnlyGrossSalaryChanged()) {
      await handleSalaryOnlyUpdate();
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/update`,
        {
          payrollId: selectedPayroll.payrollId,
          employeeId: employee.id,
          grossSalary: parseFloat(grossSalary),
          branchId: 0,
          employeePaymentSetupDTO: components.map((component) => ({
            paymentSetupId: component.paymentSetupId || 0,
            salaryName: component.salaryName,
            value: parseFloat(component.value),
          })),
        },
      );
      fetchEmployeeData();
      closeModal();
      showToast("Payroll updated successfully!", "success");
    } catch (error) {
      console.error("Error updating payroll:", error);
      showToast(
        error.response?.data?.message ||
          "An error occurred. Please try again later.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAutoSubmit = async () => {
    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType,
    );
    if (selectedPayroll && responseData) {
      // Check if only gross salary changed for auto payroll
      if (isOnlyGrossSalaryChanged()) {
        await handleSalaryOnlyUpdate();
        return;
      }

      setIsSubmitting(true);
      try {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/update`,
          {
            payrollId: selectedPayroll.payrollId,
            employeeId: employee.id,
            grossSalary: parseFloat(grossSalary),
            branchId: 0,
            employeePaymentSetupDTO: [],
          },
        );
        fetchEmployeeData();
        closeModal();
        showToast("Payroll updated successfully!", "success");
      } catch (error) {
        console.error("Error updating automatic payroll:", error);
        showToast(
          error.response?.data?.message ||
            "An error occurred. Please try again later.",
          "error",
        );
      } finally {
        setIsSubmitting(false);
      }
    } else {
      showToast("Please calculate payroll before submitting.", "error");
    }
  };

  const handleLegacySubmit = async () => {
    // Check if only gross salary changed for legacy mode
    if (isOnlyGrossSalaryChanged()) {
      await handleSalaryOnlyUpdate();
      return;
    }

    setIsSubmitting(true);
    try {
      // Build the request payload
      const payload = {
        employeeId: employee.id,
        grossSalary: parseFloat(grossSalary),
        netSalary: parseFloat(netSalary),
        employeeDeductions: parseFloat(employeeDeductions),
        employerDeductions: parseFloat(employerDeductions),
        employeePaymentSetupDTO: components.map((component) => ({
          paymentSetupId: component.paymentSetupId,
          salaryName: component.salaryName,
          value: parseFloat(component.value || component.amount),
        })),
      };

      // Include payrollId if it exists
      if (currentPayrollId) {
        payload.payrollId = currentPayrollId;
      }

      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/update`,
        payload,
      );
      showToast("Payroll updated successfully.");
      fetchEmployeeData();
      closeModal();
    } catch (error) {
      console.error("Error updating payroll:", error);
      showToast(
        error.response?.data?.message ||
          "An error occurred. Please try again later.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const renderComponentsInputs = () =>
    components.map((component, index) => (
      <div key={index} className="form-group">
        <label>
          {component.salaryName}
          {component.contributionType &&
            ` - ${capitalize(component.contributionType)}`}
          :
        </label>
        <input
          type="number"
          value={component.value || ""}
          placeholder={`Enter ${component.salaryName.toLowerCase()} amount`}
          onChange={(e) => {
            const updatedComponents = [...components];
            updatedComponents[index].value = e.target.value;
            setComponents(updatedComponents);
          }}
        />
      </div>
    ));

  const renderAutoComponents = () =>
    components.map((component, index) => (
      <div key={index} className="salary-component-row">
        <span className="salary-name">{component.salaryName}</span>

        <span className="salary-meta">
          {component.contributionType
            ? capitalize(component.contributionType)
            : ""}
        </span>

        <span className="salary-amount">
          ₹{Number(component.amount).toLocaleString()}
        </span>
      </div>
    ));

  const getCurrentPayrollSetupType = () => {
    if (!payrollType) return null;
    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType,
    );
    return selectedPayroll?.payrollSetupType;
  };

  const isCurrentPayrollChanged = () => {
    if (!payrollType) return false;
    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType,
    );
    return selectedPayroll?.payrollId !== currentPayrollId;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="edit-payroll-modal-overlay">
      <PayrollChangeWarningPopup
        isOpen={showWarningPopup}
        onConfirm={handleWarningConfirm}
        onCancel={handleWarningCancel}
        payrollName={pendingPayrollChange?.payrollType || ""}
      />

      <div className="edit-payroll-modal">
        <button className="close-button" onClick={closeModal}>
          &times;
        </button>
        <h2>Edit Payroll for {employee.name}</h2>
        <div className="edit-payroll-form">
          <div className="form-group">
            <label htmlFor="grossSalary">Gross Salary:</label>
            <input
              id="grossSalary"
              type="number"
              value={grossSalary}
              onChange={handleGrossSalaryChange}
              placeholder="Enter gross salary"
            />
          </div>

          {/* Show warning when gross salary is changed for existing payroll */}
          {grossSalaryChanged && (
            <div className="payroll-change-notice">
              <p>
                💡 You have modified the gross salary. Please submit the changes
                to update the payroll.
              </p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="payrollType">Payroll Type:</label>
            <Select
              id="payrollType"
              value={selectedPayroll}
              onChange={(option) => {
                setSelectedPayroll(option);
                const syntheticEvent = {
                  target: {
                    value: option?.payrollName || "",
                  },
                };
                handlePayrollTypeChange(syntheticEvent);
              }}
              options={payrollOptions.map((o) => ({
                ...o,
                label: `${o.payrollName} – ${o.payrollSetupType}`,
                value: o.payrollId,
              }))}
              placeholder="Select payroll type…"
              isClearable
              classNamePrefix="react-select"
            />
          </div>

          {/* Show warning when payroll type is changed */}
          {isCurrentPayrollChanged() && (
            <div className="payroll-change-notice">
              <p>
                ⚠️ You have selected a different payroll type. The payroll
                components will be recalculated and YTD amounts will reset to 0.
              </p>
            </div>
          )}

          {/* AUTO Payroll - Show calculated summary */}
          {payrollType && getCurrentPayrollSetupType() === "AUTO" && (
            <div>
              {isCalculating && (
                <div className="loading-message">Calculating payroll...</div>
              )}

              {responseData && (
                <>
                  {/* 🔹 ADD THIS */}
                  {components.length > 0 && (
                    <div className="auto-components">
                      <h3>Salary Components</h3>
                      {renderAutoComponents()}
                    </div>
                  )}

                  <div className="auto-payroll-summary">
                    <h3>Payroll Summary</h3>

                    <div className="summary-item">
                      <strong>Gross Salary:</strong> ₹
                      {responseData.data.grossSalary?.toLocaleString() || 0}
                    </div>

                    <div className="summary-item">
                      <strong>Net Salary:</strong> ₹
                      {responseData.data.netSalary?.toLocaleString() || 0}
                    </div>

                    <div className="summary-item">
                      <strong>Employee Deductions:</strong> ₹
                      {responseData.data.employeeDeductions?.toLocaleString() ||
                        0}
                    </div>

                    <div className="summary-item">
                      <strong>Employer Deductions:</strong> ₹
                      {responseData.data.employerDeductions?.toLocaleString() ||
                        0}
                    </div>

                    {responseData.data.ctc && (
                      <div className="summary-item">
                        <strong>CTC:</strong> ₹
                        {responseData.data.ctc?.toLocaleString() || 0}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* MANUAL Payroll - Show input fields */}
          {payrollType && getCurrentPayrollSetupType() === "MANUAL" && (
            <div className="manual-payroll-inputs">
              {isCalculating && (
                <div className="loading-message">
                  Loading payroll components...
                </div>
              )}

              {/* Show components if they exist - either from original data or after calculation */}
              {!isCalculating && components.length > 0 && (
                <>{renderComponentsInputs()}</>
              )}

              {/* Show message if no components and not calculating */}
              {!isCalculating && components.length === 0 && (
                <div className="no-components-message">
                  <p>
                    No payroll components found. Please check the payroll setup.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Legacy/Current Payroll - Show existing fields only when no payroll type is selected */}
          {!payrollType && (
            <>
              <div className="form-group">
                <label htmlFor="netSalary">Net Salary:</label>
                <input
                  id="netSalary"
                  type="number"
                  value={netSalary}
                  onChange={(e) => setNetSalary(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="employeeDeductions">Employee Deductions:</label>
                <input
                  id="employeeDeductions"
                  type="number"
                  value={employeeDeductions}
                  onChange={(e) => setEmployeeDeductions(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="employerDeductions">Employer Deductions:</label>
                <input
                  id="employerDeductions"
                  type="number"
                  value={employerDeductions}
                  onChange={(e) => setEmployerDeductions(e.target.value)}
                />
              </div>
              {components.map((component, index) => (
                <div className="form-group" key={index}>
                  <label>{component.salaryName}:</label>
                  <input
                    type="number"
                    value={component.value || component.amount || ""}
                    onChange={(e) =>
                      setComponents((prev) => {
                        const updated = [...prev];
                        updated[index].value = e.target.value;
                        return updated;
                      })
                    }
                  />
                </div>
              ))}
            </>
          )}

          {/* Submit Buttons */}
          <div className="form-group">
            {payrollType &&
            getCurrentPayrollSetupType() === "AUTO" &&
            responseData ? (
              <ButtonWithIcon
                text={isSubmitting ? "Updating..." : "Update Payroll"}
                onClick={handleAutoSubmit}
                disabled={isSubmitting}
                className="success"
              />
            ) : payrollType &&
              getCurrentPayrollSetupType() === "MANUAL" &&
              components.length > 0 ? (
              <ButtonWithIcon
                text={isSubmitting ? "Updating..." : "Update Payroll"}
                onClick={handleManualSubmit}
                disabled={isSubmitting || !validateComponents()}
                className="success"
              />
            ) : (
              <button
                className="submit-button"
                onClick={handleLegacySubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Updating..." : "Update Payroll"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPayroll;
